import { useMemo, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  type LeaveItem,
  type LeaveStatus,
  type SickLeaveItem,
  fetchLeavesDashboard,
} from "./api";

type FilterValue = "all" | LeaveStatus;
const FILTERS: FilterValue[] = ["all", "REQUESTED", "APPROVED", "REJECTED", "CANCELLED"];

export function MyLeavesPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";

  const query = useQuery({
    queryKey: ["leaves-my"],
    queryFn: fetchLeavesDashboard,
    refetchOnWindowFocus: true,
  });

  const [filter, setFilter] = useState<FilterValue>("all");

  const data = query.data;

  const filteredLeaves = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.leaves;
    return data.leaves.filter((l) => l.status === filter);
  }, [data, filter]);

  return (
    <div className="leaves-root">
      <h1 className="page-title">{t("leaves.title")}</h1>
      <p className="page-subtitle">{t("leaves.subtitle")}</p>

      {query.isLoading && !data && <p className="page-subtitle">{t("common.loading")}</p>}
      {query.isError && <p className="flash flash-error">{t("common.error.network")}</p>}

      {data && (
        <>
          <div className="leaves-top-row">
            <BalanceCard
              year={data.year}
              total={data.balance.total_days}
              used={data.balance.used_days}
              remaining={data.balance.remaining_days}
            />
            <CalendarCard
              leaves={data.leaves}
              sickLeaves={data.sick_leaves}
              locale={locale}
            />
          </div>

          {data.sick_leaves.length > 0 && (
            <SickLeavesCard items={data.sick_leaves} locale={locale} />
          )}

          <RequestsCard
            allCount={data.leaves.length}
            shownCount={filteredLeaves.length}
            filter={filter}
            onFilter={setFilter}
            leaves={filteredLeaves}
            locale={locale}
          />
        </>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Balance card
// ───────────────────────────────────────────────────────────────

function BalanceCard({
  year,
  total,
  used,
  remaining,
}: {
  year: number;
  total: number;
  used: number;
  remaining: number;
}) {
  const { t } = useTranslation();
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div className="card leaves-balance">
      <div className="leaves-balance-header">
        <div>
          <div className="summary-card-title">
            {t("leaves.year")} {year}
          </div>
          <div className="summary-card-meta">{t("leaves.balance_overview")}</div>
        </div>
        <div className="leaves-cta">
          <Link to="/leaves/new" className="btn btn-cta">
            {t("leaves.request_new")}
          </Link>
          <div className="summary-card-meta">{t("leaves.request_new_hint")}</div>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">{t("leaves.kpi.remaining")}</div>
          <div className="kpi-value">{remaining}</div>
          <div className="summary-card-meta">{t("leaves.days")}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">{t("leaves.kpi.used")}</div>
          <div className="kpi-value">{used}</div>
          <div className="summary-card-meta">{t("leaves.days")}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">{t("leaves.kpi.total")}</div>
          <div className="kpi-value">{total}</div>
          <div className="summary-card-meta">{t("leaves.days")}</div>
        </div>
      </div>

      <div className="leaves-progress">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${pct}%` }}></div>
        </div>
        <div className="summary-card-meta">
          {t("leaves.used_progress", { used, total })}
        </div>
      </div>
      <div className="summary-card-meta leaves-note">
        {t("leaves.pending_note")}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Calendar card
// ───────────────────────────────────────────────────────────────

interface CalEvent {
  kind: "leave" | "sick";
  start: string;
  end: string | null;
  status: string;
}

function CalendarCard({
  leaves,
  sickLeaves,
  locale,
}: {
  leaves: LeaveItem[];
  sickLeaves: SickLeaveItem[];
  locale: string;
}) {
  const { t } = useTranslation();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [view, setView] = useState({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  const events: CalEvent[] = useMemo(() => {
    const e: CalEvent[] = [];
    leaves.forEach((l) =>
      e.push({ kind: "leave", start: l.start_date, end: l.end_date, status: l.status })
    );
    sickLeaves.forEach((sl) => {
      if (sl.status !== "cancelled") {
        e.push({ kind: "sick", start: sl.start_date, end: sl.end_date, status: sl.status });
      }
    });
    return e;
  }, [leaves, sickLeaves]);

  const monthLabel = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    return first.toLocaleDateString(locale, { month: "long", year: "numeric" });
  }, [view, locale]);

  const dayNames = (t("leaves.cal.day_short", { returnObjects: true }) as string[]) ?? [
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
  ];

  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const startDow = (first.getDay() + 6) % 7; // Mon=0
    const arr: Array<{ day: number | null; cls: string }> = [];
    for (let i = 0; i < startDow; i++) arr.push({ day: null, cls: "" });
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${view.year}-${String(view.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const isToday =
        view.year === today.getFullYear() &&
        view.month === today.getMonth() &&
        day === today.getDate();
      let leaveStatus: string | null = null;
      let hasSick = false;
      events.forEach((e) => {
        const effEnd = e.end ?? "9999-12-31";
        if (dateStr < e.start || dateStr > effEnd) return;
        if (e.kind === "leave") {
          if (e.status === "APPROVED") leaveStatus = "APPROVED";
          else if (e.status === "REJECTED" && leaveStatus !== "APPROVED")
            leaveStatus = "REJECTED";
          else if (e.status === "REQUESTED" && !leaveStatus) leaveStatus = "REQUESTED";
        } else if (e.kind === "sick") {
          hasSick = true;
        }
      });
      let cls = "cal-day";
      if (isToday) cls += " cal-today";
      if (hasSick) cls += " cal-sick";
      else if (leaveStatus === "APPROVED") cls += " cal-approved";
      else if (leaveStatus === "REJECTED") cls += " cal-rejected";
      else if (leaveStatus === "REQUESTED") cls += " cal-requested";
      arr.push({ day, cls });
    }
    return arr;
  }, [view, events, today]);

  const prev = () =>
    setView((v) => (v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 }));
  const next = () =>
    setView((v) => (v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 }));

  return (
    <div className="card leaves-calendar-card">
      <div className="table-toolbar">
        <div className="table-toolbar-left">
          <h3 className="summary-card-title" style={{ margin: 0 }}>
            {t("leaves.calendar")}
          </h3>
        </div>
        <div className="table-toolbar-right">
          <button type="button" className="btn btn-secondary btn-sm" onClick={prev}>
            ◀
          </button>
          <span
            style={{
              fontWeight: 600,
              minWidth: 120,
              textAlign: "center",
              display: "inline-block",
              textTransform: "capitalize",
            }}
          >
            {monthLabel}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={next}>
            ▶
          </button>
        </div>
      </div>
      <div className="leaves-calendar-grid">
        {dayNames.map((d) => (
          <div key={d} className="cal-header">
            {d}
          </div>
        ))}
        {cells.map((c, idx) =>
          c.day === null ? (
            <div key={`pad-${idx}`} />
          ) : (
            <div key={`d-${c.day}`} className={c.cls}>
              {c.day}
            </div>
          )
        )}
      </div>
      <div className="leaves-calendar-legend">
        <span>
          <span className="legend-dot" style={{ background: "#22c55e" }}></span>{" "}
          {t("leaves.legend.approved")}
        </span>
        <span>
          <span className="legend-dot" style={{ background: "#f59e0b" }}></span>{" "}
          {t("leaves.legend.rejected")}
        </span>
        <span>
          <span className="legend-dot" style={{ background: "#3b82f6" }}></span>{" "}
          {t("leaves.legend.pending")}
        </span>
        <span>
          <span className="legend-dot" style={{ background: "#e74c3c" }}></span>{" "}
          {t("leaves.legend.sick")}
        </span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Sick leaves card
// ───────────────────────────────────────────────────────────────

function SickLeavesCard({
  items,
  locale,
}: {
  items: SickLeaveItem[];
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <div className="table-toolbar">
        <div className="table-toolbar-left">
          <h3 className="summary-card-title" style={{ margin: 0 }}>
            {t("leaves.sick_leaves")}
          </h3>
          <span className="summary-card-meta">
            {items.length} {t("leaves.records")}
          </span>
        </div>
      </div>
      <div className="table-wrapper">
        <table className="table table-sticky">
          <thead>
            <tr>
              <th>{t("leaves.col.dates")}</th>
              <th>{t("leaves.col.type")}</th>
              <th>{t("leaves.col.status")}</th>
              <th>{t("leaves.col.reason")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((sl) => (
              <tr key={sl.id}>
                <td>
                  {formatDate(sl.start_date, locale)}
                  {" → "}
                  {sl.end_date ? (
                    formatDate(sl.end_date, locale)
                  ) : (
                    <em>{t("leaves.ongoing")}</em>
                  )}
                </td>
                <td>{t(`leaves.sick_leave_type.${sl.type}`, { defaultValue: sl.type })}</td>
                <td>
                  <SickStatusPill status={sl.status} />
                </td>
                <td>{sl.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SickStatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  if (status === "active")
    return <span className="status-pill status-requested">{t("leaves.sick_status.active")}</span>;
  if (status === "closed")
    return <span className="status-pill status-approved">{t("leaves.sick_status.closed")}</span>;
  if (status === "cancelled")
    return <span className="status-pill status-cancelled">{t("leaves.sick_status.cancelled")}</span>;
  return <span className="status-pill">{status}</span>;
}

// ───────────────────────────────────────────────────────────────
// Requests card (filters + table)
// ───────────────────────────────────────────────────────────────

function RequestsCard({
  allCount,
  shownCount,
  filter,
  onFilter,
  leaves,
  locale,
}: {
  allCount: number;
  shownCount: number;
  filter: FilterValue;
  onFilter: (f: FilterValue) => void;
  leaves: LeaveItem[];
  locale: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div className="card">
      <div className="table-toolbar leaves-table-toolbar">
        <div className="table-toolbar-left">
          <h3 className="summary-card-title" style={{ margin: 0 }}>
            {t("leaves.requests_title")}
          </h3>
          <span className="summary-card-meta">
            {t("leaves.showing", { shown: shownCount, total: allCount })}
          </span>
        </div>
        <div className="table-toolbar-right">
          <div className="leave-filter-chips">
            {FILTERS.map((f) => {
              const key =
                f === "all"
                  ? "leaves.filter.all"
                  : f === "REQUESTED"
                    ? "leaves.filter.pending"
                    : `leaves.filter.${f.toLowerCase()}`;
              return (
                <button
                  key={f}
                  type="button"
                  className={"chip chip-toggle" + (filter === f ? " chip-active" : "")}
                  onClick={() => onFilter(f)}
                >
                  {t(key)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {allCount === 0 ? (
        <p className="page-subtitle" style={{ margin: 0 }}>
          {t("leaves.empty.all")}
        </p>
      ) : leaves.length === 0 ? (
        <p className="page-subtitle">{t("leaves.empty.filter")}</p>
      ) : (
        <div className="table-wrapper">
          <table className="table table-sticky">
            <thead>
              <tr>
                <th>{t("leaves.col.dates")}</th>
                <th>{t("leaves.col.days")}</th>
                <th>{t("leaves.col.type")}</th>
                <th>{t("leaves.col.status")}</th>
                <th>{t("leaves.col.created")}</th>
                <th>{t("leaves.col.decided")}</th>
                <th>{t("leaves.col.details")}</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((l) => {
                const isOpen = expanded === l.id;
                return (
                  <Fragment key={l.id}>
                    <tr>
                      <td>
                        <span className="leave-range-main">
                          {formatDate(l.start_date, locale)} →{" "}
                          {formatDate(l.end_date, locale)}
                        </span>
                      </td>
                      <td>{l.days}</td>
                      <td>{t(`leaves.type.${l.type}`, { defaultValue: titleCase(String(l.type)) })}</td>
                      <td>
                        <LeaveStatusPill status={l.status} />
                      </td>
                      <td>
                        {l.created_at ? (
                          <span className="leave-dt">
                            {formatDateTime(l.created_at, locale)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {l.decided_at ? (
                          <span className="leave-dt">
                            {formatDateTime(l.decided_at, locale)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setExpanded(isOpen ? null : l.id)}
                        >
                          {t("leaves.col.details")}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${l.id}-details`}>
                        <td colSpan={7}>
                          <div className="row-details-body">
                            <div>
                              <strong>{t("leaves.detail.reason")}:</strong>{" "}
                              {l.reason || "—"}
                            </div>
                            <div>
                              <strong>{t("leaves.detail.created_at")}:</strong>{" "}
                              {l.created_at
                                ? formatDateTime(l.created_at, locale)
                                : "—"}
                            </div>
                            <div>
                              <strong>{t("leaves.detail.decided_at")}:</strong>{" "}
                              {l.decided_at
                                ? formatDateTime(l.decided_at, locale)
                                : "—"}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LeaveStatusPill({ status }: { status: LeaveStatus }) {
  const { t } = useTranslation();
  const map: Record<LeaveStatus, { cls: string; key: string }> = {
    APPROVED: { cls: "status-approved", key: "leaves.status.approved" },
    REJECTED: { cls: "status-rejected", key: "leaves.status.rejected" },
    CANCELLED: { cls: "status-cancelled", key: "leaves.status.cancelled" },
    REQUESTED: { cls: "status-requested", key: "leaves.status.requested" },
  };
  const m = map[status] || map.REQUESTED;
  return <span className={`status-pill ${m.cls}`}>{t(m.key)}</span>;
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatDate(iso: string, locale: string): string {
  try {
    const d = new Date(iso + "T00:00:00");
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(d);
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string, locale: string): string {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(d);
  } catch {
    return iso;
  }
}
